import React, { useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Select as MuiSelect,
  Theme,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import {
  CheckCircle,
  Cancel,
  Close,
  CallSplit,
  DescriptionOutlined,
  LockOutlined,
  KeyboardArrowDown,
  KeyboardArrowUp,
  TuneRounded,
  Visibility,
  VisibilityOff,
} from '@material-ui/icons';
import { Form, Formik } from 'formik';
import pluralize from 'pluralize';
import { TabletButton } from '@campfire/tablet-button';
import { useCampfireQuery } from '../../../../global/network/useCampfireQuery';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { useOrgInfo } from '../../../../global/auth/use-org-info/use-org-info';
import { AlertCard, getAlertCardColors } from '../../../../common/cards/alert-card/AlertCard';
import { TaskFormFieldList } from '../../../../common/form/task-form/TaskFormFieldList';
import { initTaskFormFormikValues } from '../../../../common/form/task-form/model/task-form-init';
import { deserializeTaskItems } from '../utils';
import {
  ADMIN_CONSOLE_VOLUNTEER_PROFILE_TASK_BUILDER_GET_ALL_PROGRAMS,
  ADMIN_CONSOLE_VOLUNTEER_PROFILE_TASK_BUILDER_GET_ALL_ROLES,
} from '../../admin-console/admin-console-content-pages/admin-console-volunteer-profile/admin-console-volunteer-profile.gql';
import { AdminConsoleVolunteerProfileTaskBuilderGetAllPrograms } from '../../admin-console/admin-console-content-pages/admin-console-volunteer-profile/__generated__/AdminConsoleVolunteerProfileTaskBuilderGetAllPrograms';
import { AdminConsoleVolunteerProfileTaskBuilderGetAllRoles } from '../../admin-console/admin-console-content-pages/admin-console-volunteer-profile/__generated__/AdminConsoleVolunteerProfileTaskBuilderGetAllRoles';
import { AdminConsoleGetSingleVolunteerProfileTask_vm_task as PreviewTask } from '../../admin-console/admin-console-content-pages/admin-console-volunteer-profile/__generated__/AdminConsoleGetSingleVolunteerProfileTask';
import { TASK_PREVIEW_GET_FUNNELS_AND_CONFIG, resolveConfigFlag } from './task-preview.gql';
import {
  TaskPreviewGetFunnelsAndConfig,
  TaskPreviewGetFunnelsAndConfig_vm_funnels as PreviewFunnel,
} from './__generated__/TaskPreviewGetFunnelsAndConfig';

type Persona = 'applicant' | 'volunteer' | 'activityLeader' | 'programManager' | 'admin';

interface Identity {
  isApplicant: boolean;
  isVolunteer: boolean;
  isActivityLeader: boolean;
  isManager: boolean;
  isAdmin: boolean;
}

const personaItems: Array<{ value: Persona; label: string }> = [
  { value: 'applicant', label: 'Applicant' },
  { value: 'volunteer', label: 'General volunteer' },
  { value: 'activityLeader', label: 'Activity leader' },
  { value: 'programManager', label: 'Program manager' },
  { value: 'admin', label: 'Admin' },
];

// A volunteer record is created at sign up, so applicants carry a volunteer identity too, which is
// what activity enrolments, activity roles and the 'gv' permission level are read from.
const getIdentity = (persona: Persona): Identity => ({
  isApplicant: persona === 'applicant',
  isVolunteer: true,
  isActivityLeader: persona === 'activityLeader',
  isManager: persona === 'programManager' || persona === 'admin',
  isAdmin: persona === 'admin',
});

interface Condition {
  label: string;
  passed: boolean;
  detail: string;
}

interface HiddenItem {
  name: string;
  programNames: string[];
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialogContent: {
      padding: 0,
      display: 'flex',
      flex: '1 1 auto',
      overflow: 'hidden',
    },
    controls: {
      width: 300,
      minWidth: 300,
      borderRight: `1px solid ${theme.color.grey.border}`,
      padding: '24px',
      overflowY: 'auto',
      boxSizing: 'border-box',
    },
    result: {
      flex: '1 1 auto',
      overflowY: 'auto',
      padding: '24px',
      backgroundColor: '#F9F9F9',
      boxSizing: 'border-box',
    },
    contentContainer: {
      background: 'white',
      paddingTop: '1.25rem',
      paddingBottom: '1.25rem',
      paddingLeft: '1.875rem',
      paddingRight: '1.875rem',
      marginTop: '25px',
    },
    contentHeader: {
      display: 'flex',
      alignItems: 'flex-start',
      cursor: 'pointer',
      userSelect: 'none',
    },
    contentTitle: {
      fontWeight: 600,
      fontSize: '16px',
      display: 'flex',
      alignItems: 'center',
    },
    contentDescription: {
      fontSize: '14px',
      color: '#808080',
    },
    thinSelect: {
      '& .MuiSelect-outlined': {
        paddingTop: 8,
        paddingBottom: 8,
        minHeight: 22,
        display: 'flex',
        alignItems: 'center',
      },
    },
    panelTitle: {
      fontSize: 16,
      fontWeight: 600,
    },
    fieldLabel: {
      fontWeight: 600,
      fontSize: 14,
      color: '#333',
      marginBottom: 6,
    },
    microLabel: {
      fontSize: 11,
      color: '#aaa',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    },
    microValue: {
      fontSize: 13,
      color: '#555',
      fontWeight: 500,
    },
    placeholder: {
      fontSize: 14,
      color: '#808080',
    },
    fieldNote: {
      fontSize: 12,
      color: '#B45309',
      marginTop: 6,
    },
  })
);

interface Props {
  open: boolean;
  onClose: () => void;
  task?: PreviewTask | null;
}

export const TaskVisibilityPreviewModal = ({ open, onClose, task }: Props) => {
  const classes = useStyles();
  const { theme } = useCampfireTheme();
  const org = useOrgInfo();

  const programWord = org?.menuLabels?.wordProgram || 'Program';
  const activityWord = org?.menuLabels?.wordActivity || 'Activity';
  const programWordPlural = pluralize(programWord);
  const activityWordPlural = pluralize(activityWord);

  const [persona, setPersona] = useState<Persona>('volunteer');
  const [funnelId, setFunnelId] = useState<string>('none');
  const [programIds, setProgramIds] = useState<string[]>([]);
  const [activityIds, setActivityIds] = useState<string[]>([]);
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const [showConditions, setShowConditions] = useState(true);
  const [showFunnelListing, setShowFunnelListing] = useState(true);
  const [showBehaviour, setShowBehaviour] = useState(false);
  const [showForm, setShowForm] = useState(true);

  const { data: programsData } = useCampfireQuery<AdminConsoleVolunteerProfileTaskBuilderGetAllPrograms, undefined>(
    ADMIN_CONSOLE_VOLUNTEER_PROFILE_TASK_BUILDER_GET_ALL_PROGRAMS,
    { options: { skip: !open } }
  );

  const { data: rolesData } = useCampfireQuery<AdminConsoleVolunteerProfileTaskBuilderGetAllRoles, undefined>(
    ADMIN_CONSOLE_VOLUNTEER_PROFILE_TASK_BUILDER_GET_ALL_ROLES,
    { options: { skip: !open } }
  );

  const { data: funnelData } = useCampfireQuery<TaskPreviewGetFunnelsAndConfig, undefined>(
    TASK_PREVIEW_GET_FUNNELS_AND_CONFIG,
    { options: { skip: !open } }
  );

  const funnels = useMemo(
    () =>
      (funnelData?.vm.funnels ?? [])
        .filter((funnel) => funnel.isActive)
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name)),
    [funnelData]
  );

  const selectedFunnel: PreviewFunnel | undefined = funnels.find((funnel) => funnel.funnelId === funnelId);

  const canEnrolActivityAtSignUp = resolveConfigFlag(funnelData?.vm.enrolActivity);
  const canApplyForRoleAtSignUp = resolveConfigFlag(funnelData?.vm.applyRole);
  const canPickMultipleProgramsAtSignUp = resolveConfigFlag(funnelData?.vm.multiProgram);

  const programs = useMemo(
    () =>
      (programsData?.vm.programs ?? [])
        .filter((program) => program.dateSuspended === null)
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name)),
    [programsData]
  );

  const activities = useMemo(
    () =>
      programs
        .filter((program) => programIds.includes(program.programId))
        .flatMap((program) => program.activities)
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name)),
    [programs, programIds]
  );

  const roles = useMemo(
    () => (rolesData?.vm.activityRoles ?? []).slice().sort((a, b) => a.name.localeCompare(b.name)),
    [rolesData]
  );

  const identity = getIdentity(persona);

  // An activity enrolment is only read through the program identity that contains it, so an activity
  // whose program is not selected never counts towards visibility.
  const orphanActivityIds = activityIds.filter((id) => !activities.some((activity) => activity.activityId === id));
  const effectiveActivityIds = activityIds.filter((id) => !orphanActivityIds.includes(id));

  const conditions: Condition[] = useMemo(() => {
    if (!task) return [];

    const taskPrograms = task.programs ?? [];
    const taskActivities = task.activities ?? [];
    const taskRoles = task.roles ?? [];
    const permissionLevel = task.permissionLevel ? `${task.permissionLevel}` : null;

    const permissionLevelPassed = (() => {
      switch (permissionLevel) {
        case 'gv':
          return identity.isVolunteer;
        case 'al':
          return identity.isActivityLeader;
        case 'pm':
          return identity.isManager;
        case 'admin':
          return identity.isAdmin;
        default:
          return true;
      }
    })();

    const permissionLevelLabel: { [key: string]: string } = {
      gv: 'General Volunteer',
      al: 'Activity Leader',
      pm: 'Program Manager',
      admin: 'Admin',
    };

    return [
      {
        label: programWordPlural,
        passed: taskPrograms.length === 0 || taskPrograms.some((p) => programIds.includes(p.programId)),
        detail:
          taskPrograms.length === 0
            ? `Open to all ${programWordPlural.toLowerCase()}`
            : `Needs one of ${taskPrograms.map((p) => p.name).join(', ')}`,
      },
      {
        label: activityWordPlural,
        passed: taskActivities.length === 0 || taskActivities.some((a) => effectiveActivityIds.includes(a.activityId)),
        detail:
          taskActivities.length === 0
            ? `Open to all ${activityWordPlural.toLowerCase()}`
            : `Needs enrolment in one of ${taskActivities.map((a) => a.name).join(', ')}`,
      },
      {
        label: 'Roles',
        passed: taskRoles.length === 0 || taskRoles.some((r) => roleIds.includes(r.activityRoleId)),
        detail:
          taskRoles.length === 0 ? 'Open to all roles' : `Needs one of ${taskRoles.map((r) => r.name).join(', ')}`,
      },
      {
        label: 'Permission level',
        passed: permissionLevelPassed,
        detail: permissionLevel
          ? `Restricted to ${permissionLevelLabel[permissionLevel] ?? permissionLevel}`
          : 'Open to every permission level',
      },
      {
        label: 'Sign up stage',
        passed: !task.applicantsOnly || identity.isApplicant,
        detail: task.applicantsOnly
          ? 'Applicants only, approved volunteers cannot see it'
          : 'Open to applicants and approved volunteers',
      },
      {
        label: 'Who completes it',
        passed: !task.managerCompleted || identity.isManager || identity.isAdmin,
        detail: task.managerCompleted
          ? 'A manager, so volunteers never see it'
          : 'The volunteer, so anyone qualifying can see it',
      },
    ];
  }, [task, programIds, effectiveActivityIds, roleIds, identity, programWordPlural, activityWordPlural]);

  const failedConditions = conditions.filter((condition) => !condition.passed);
  const isVisible = identity.isAdmin || failedConditions.length === 0;
  const verdictColors = getAlertCardColors(isVisible ? 'success' : 'urgent');

  // Mirrors GetFunnelBySlugHandler::getApplicableTasks, which advertises tasks on the funnel landing page
  // with OR semantics across the three dimensions rather than the AND used once the applicant signs in.
  const funnelListing = useMemo(() => {
    if (!task || !selectedFunnel) return null;

    if (task.isHidden) return { listed: false, reason: 'Hidden tasks are never advertised on a funnel page' };
    if (task.adminOnly) return { listed: false, reason: 'Admin only tasks are never advertised on a funnel page' };
    if (task.managerCompleted) {
      return { listed: false, reason: 'Manager completed tasks are never advertised on a funnel page' };
    }

    const taskProgramIds = (task.programs ?? []).map((p) => p.programId);
    const taskActivityIds = (task.activities ?? []).map((a) => a.activityId);
    const taskRoleIds = (task.roles ?? []).map((r) => r.activityRoleId);

    if (taskProgramIds.length === 0 && taskActivityIds.length === 0 && taskRoleIds.length === 0) {
      return { listed: true, reason: 'The task has no restrictions, so every funnel page lists it' };
    }

    const funnelProgramIds = selectedFunnel.programs.map((p) => p.programId);
    const funnelActivityIds = selectedFunnel.activities.map((a) => a.activityId);
    const funnelRoleIds = selectedFunnel.activityRoles.map((r) => r.activityRoleId);

    if (funnelProgramIds.length === 0 && funnelActivityIds.length === 0 && funnelRoleIds.length === 0) {
      return { listed: true, reason: 'The funnel pins nothing, so it lists every applicable task' };
    }

    const overlaps = [
      taskProgramIds.some((id) => funnelProgramIds.includes(id)) ? programWordPlural.toLowerCase() : null,
      taskActivityIds.some((id) => funnelActivityIds.includes(id)) ? activityWordPlural.toLowerCase() : null,
      taskRoleIds.some((id) => funnelRoleIds.includes(id)) ? 'roles' : null,
    ].filter((match): match is string => match !== null);

    if (overlaps.length > 0) {
      return { listed: true, reason: `The funnel overlaps the task on ${overlaps.join(', ')}` };
    }
    return { listed: false, reason: 'The funnel shares no programs, activities or roles with this task' };
  }, [task, selectedFunnel, programWordPlural, activityWordPlural]);

  const deserializedTaskItems = useMemo(() => {
    if (!task || task.taskItems.length === 0) return null;
    return deserializeTaskItems([...task.taskItems].sort((a, b) => a.order - b.order));
  }, [task]);

  const initialFormValues = useMemo(() => {
    const taskItems: any[] = task ? task.taskItems : [];
    return initTaskFormFormikValues(taskItems);
  }, [task]);

  // Mirrors the filter TaskFormFieldList applies, so this list is exactly what drops out of the rendered form.
  const { hiddenItems, visibleItemCount, totalItemCount } = useMemo(() => {
    if (!deserializedTaskItems) return { hiddenItems: [] as HiddenItem[], visibleItemCount: 0, totalItemCount: 0 };

    const rootItems: any[] = deserializedTaskItems.allIds
      .filter((itemId) => !deserializedTaskItems.byTriggerOption[itemId])
      .map((itemId) => deserializedTaskItems.byId[itemId]);

    const hidden: HiddenItem[] = [];
    rootItems.forEach((item) => {
      const hiddenFor: string[] = item.hiddenForProgramIds || [];
      const matched = programIds.filter((programId) => hiddenFor.includes(programId));
      if (matched.length === 0) return;
      hidden.push({
        name: item.field?.name || item.heading || item.media?.name || 'Content block',
        programNames: matched.map(
          (programId) => programs.find((program) => program.programId === programId)?.name || programWord
        ),
      });
    });

    return {
      hiddenItems: hidden,
      visibleItemCount: rootItems.length - hidden.length,
      totalItemCount: rootItems.length,
    };
  }, [deserializedTaskItems, programIds, programs, programWord]);

  const behaviourNotes: Array<{ label: string; value: string }> = task
    ? [
        { label: 'Hidden task', value: task.isHidden ? 'Yes' : 'No' },
        { label: 'Submissions', value: task.adminOnly ? 'Admins only' : 'Visible to managers' },
        { label: 'Blocks progress', value: task.blocksProgress ? 'Yes, until approved' : 'No' },
        {
          label: 'Task stage',
          value: task.taskStage ? `Stage ${task.taskStage.order}: ${task.taskStage.name}` : 'No stage',
        },
        { label: 'Extra rules', value: task.hasRules ? 'Configured, not simulated' : 'None' },
      ]
    : [];

  const applyFunnel = (nextFunnelId: string) => {
    setFunnelId(nextFunnelId);
    const funnel = funnels.find((item) => item.funnelId === nextFunnelId);
    if (!funnel) return;
    setProgramIds(funnel.programs.map((program) => program.programId));
    setActivityIds(funnel.activities.map((activity) => activity.activityId));
    setRoleIds(funnel.activityRoles.map((role) => role.activityRoleId));
  };

  const programNote = (() => {
    if (selectedFunnel?.lockPrograms) return `Pinned and locked by the ${selectedFunnel.name} funnel`;
    if (!identity.isApplicant) return null;
    if (programIds.length > 1 && !canPickMultipleProgramsAtSignUp && !selectedFunnel) {
      return `Multi ${programWord.toLowerCase()} applications are off, so an applicant can only hold one`;
    }
    return null;
  })();

  const activityNote = (() => {
    if (selectedFunnel?.lockActivities) return `Pinned and locked by the ${selectedFunnel.name} funnel`;
    if (orphanActivityIds.length > 0) {
      const names = orphanActivityIds.map(
        (id) => selectedFunnel?.activities.find((activity) => activity.activityId === id)?.name || activityWord
      );
      return `${names.join(', ')} sit outside the selected ${programWordPlural.toLowerCase()}, so they will not count`;
    }
    if (!identity.isApplicant || activityIds.length === 0) return null;
    if (!canEnrolActivityAtSignUp && !(selectedFunnel && selectedFunnel.activities.length > 0)) {
      return `Applicants cannot pick ${activityWordPlural.toLowerCase()} during sign up, so only a funnel can assign these`;
    }
    return null;
  })();

  const roleNote = (() => {
    if (selectedFunnel?.lockRoles) return `Pinned and locked by the ${selectedFunnel.name} funnel`;
    if (!identity.isApplicant || roleIds.length === 0) return null;
    if (!canApplyForRoleAtSignUp && !(selectedFunnel && selectedFunnel.activityRoles.length > 0)) {
      return 'Applicants cannot apply for roles during sign up, so only a funnel can assign these';
    }
    return null;
  })();

  const renderMultiSelect = (
    label: string,
    values: string[],
    onChange: (next: string[]) => void,
    items: Array<{ id: string; name: string }>,
    emptyLabel: string,
    lockedByFunnel?: boolean,
    note?: string | null
  ) => (
    <Box mb={2.5}>
      <Typography className={classes.fieldLabel}>
        {label}
        {lockedByFunnel && <LockOutlined style={{ fontSize: 13, marginLeft: 5, color: '#aaa' }} />}
      </Typography>
      <MuiSelect
        multiple
        displayEmpty
        fullWidth
        variant='outlined'
        className={classes.thinSelect}
        disabled={items.length === 0 || lockedByFunnel}
        value={values}
        onChange={(event) => onChange(event.target.value as string[])}
        renderValue={(selected) => {
          const ids = selected as string[];
          if (ids.length === 0) return <Typography className={classes.placeholder}>{emptyLabel}</Typography>;
          return (
            <Box display='flex' flexWrap='wrap' style={{ gap: 4 }}>
              {ids.map((id) => {
                const item = items.find((option) => option.id === id);
                return item ? <Chip key={item.id} size='small' color='primary' label={item.name} /> : null;
              })}
            </Box>
          );
        }}
      >
        {items.map((item) => (
          <MenuItem key={item.id} value={item.id}>
            <Checkbox color='primary' size='small' checked={values.includes(item.id)} />
            <Typography style={{ fontSize: 14 }}>{item.name}</Typography>
          </MenuItem>
        ))}
      </MuiSelect>
      {note && <Typography className={classes.fieldNote}>{note}</Typography>}
    </Box>
  );

  const renderContentBox = (
    icon: React.ReactNode,
    title: string,
    description: string,
    expanded: boolean,
    onToggle: () => void,
    children: React.ReactNode
  ) => (
    <Box className={classes.contentContainer}>
      <Box className={classes.contentHeader} onClick={onToggle}>
        <Box flex={1}>
          <Typography className={classes.contentTitle}>
            {icon} {title}
          </Typography>
          <Typography variant='body2' className={classes.contentDescription}>
            {description}
          </Typography>
        </Box>
        {expanded ? (
          <KeyboardArrowUp style={{ fontSize: 20, color: theme.color.grey.neutral200 }} />
        ) : (
          <KeyboardArrowDown style={{ fontSize: 20, color: theme.color.grey.neutral200 }} />
        )}
      </Box>
      <Collapse in={expanded}>
        <Box marginTop='1.5rem'>{children}</Box>
      </Collapse>
    </Box>
  );

  if (!task) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='lg'
      fullWidth
      PaperProps={{ style: { height: '90vh', maxHeight: '90vh' } }}
    >
      <Box display='flex' alignItems='center' padding='20px 24px'>
        <Visibility style={{ marginRight: 10, fontSize: 20, color: theme.color.grey.neutral200 }} />
        <Box flex={1}>
          <Typography className={classes.panelTitle}>{`Preview: ${task.title}`}</Typography>
          <Typography variant='body2' color='textSecondary'>
            Build a user on the left to see whether this task reaches them, and what they would see.
          </Typography>
        </Box>
        <IconButton onClick={onClose} size='small'>
          <Close />
        </IconButton>
      </Box>
      <Divider />

      <DialogContent className={classes.dialogContent}>
        <Box className={classes.controls}>
          <Box mb={2.5}>
            <Typography className={classes.fieldLabel}>User type</Typography>
            <MuiSelect
              fullWidth
              variant='outlined'
              className={classes.thinSelect}
              value={persona}
              onChange={(event) => setPersona(event.target.value as Persona)}
            >
              {personaItems.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  <Typography style={{ fontSize: 14 }}>{item.label}</Typography>
                </MenuItem>
              ))}
            </MuiSelect>
          </Box>

          <Box mb={2.5}>
            <Typography className={classes.fieldLabel}>Arrives via</Typography>
            <MuiSelect
              fullWidth
              variant='outlined'
              className={classes.thinSelect}
              value={funnelId}
              onChange={(event) => applyFunnel(`${event.target.value}`)}
            >
              <MenuItem value='none'>
                <Typography style={{ fontSize: 14 }}>Direct sign up</Typography>
              </MenuItem>
              {funnels.map((funnel) => (
                <MenuItem key={funnel.funnelId} value={funnel.funnelId}>
                  <Typography style={{ fontSize: 14 }}>{funnel.name}</Typography>
                </MenuItem>
              ))}
            </MuiSelect>
          </Box>

          {renderMultiSelect(
            programWordPlural,
            programIds,
            (next) => {
              setProgramIds(next);
              const remaining = programs
                .filter((program) => next.includes(program.programId))
                .flatMap((program) => program.activities.map((activity) => activity.activityId));
              setActivityIds((prev) => prev.filter((activityId) => remaining.includes(activityId)));
            },
            programs.map((program) => ({ id: program.programId, name: program.name })),
            `No ${programWordPlural.toLowerCase()}`,
            selectedFunnel?.lockPrograms,
            programNote
          )}

          {renderMultiSelect(
            activityWordPlural,
            activityIds,
            setActivityIds,
            activities.map((activity) => ({ id: activity.activityId, name: activity.name })),
            `No ${activityWordPlural.toLowerCase()}`,
            selectedFunnel?.lockActivities,
            activityNote
          )}

          {renderMultiSelect(
            'Roles',
            roleIds,
            setRoleIds,
            roles.map((role) => ({ id: role.activityRoleId, name: role.name })),
            'No roles',
            selectedFunnel?.lockRoles,
            roleNote
          )}
        </Box>

        <Box className={classes.result}>
          <Box display='flex' bgcolor={verdictColors.backgroundColor} style={{ borderRadius: '6px', padding: '20px' }}>
            {isVisible ? (
              <Visibility style={{ height: '20px', paddingRight: '14px', color: verdictColors.iconColor }} />
            ) : (
              <VisibilityOff style={{ height: '20px', paddingRight: '14px', color: verdictColors.iconColor }} />
            )}
            <Box>
              <Typography
                variant='subtitle2'
                style={{ fontSize: '14px', fontWeight: 700, color: verdictColors.darkTextColor }}
              >
                {isVisible ? 'This task is visible' : 'This task is not visible'}
              </Typography>
              <Typography style={{ fontSize: '13px', color: verdictColors.lightTextColor }}>
                {identity.isAdmin
                  ? 'Admins bypass every visibility condition'
                  : isVisible
                  ? 'Every condition below is satisfied'
                  : `Blocked by ${failedConditions.map((condition) => condition.label.toLowerCase()).join(', ')}`}
              </Typography>
            </Box>
          </Box>

          {renderContentBox(
            <Visibility style={{ fontSize: '14px', paddingRight: '5px' }} />,
            'Visibility conditions',
            'A volunteer must satisfy every condition to see this task',
            showConditions,
            () => setShowConditions(!showConditions),
            conditions.map((condition) => (
              <Box key={condition.label} display='flex' alignItems='flex-start' mb={2}>
                <Box flex={1} pr={2}>
                  <Typography style={{ fontWeight: 500, fontSize: '14px' }}>{condition.label}</Typography>
                  <Typography variant='body2' style={{ fontSize: '13px', color: '#808080' }}>
                    {condition.detail}
                  </Typography>
                </Box>
                {condition.passed ? (
                  <CheckCircle style={{ fontSize: 18, color: theme.alert.green.light, marginTop: 2 }} />
                ) : (
                  <Cancel style={{ fontSize: 18, color: theme.alert.red.light, marginTop: 2 }} />
                )}
              </Box>
            ))
          )}

          {funnelListing &&
            renderContentBox(
              <CallSplit style={{ fontSize: '14px', paddingRight: '5px' }} />,
              'Funnel landing page',
              `Whether the ${selectedFunnel?.name} page advertises this task before anyone signs up`,
              showFunnelListing,
              () => setShowFunnelListing(!showFunnelListing),
              <>
                <Box display='flex' alignItems='flex-start' mb={2}>
                  <Box flex={1} pr={2}>
                    <Typography style={{ fontWeight: 500, fontSize: '14px' }}>
                      {funnelListing.listed ? 'Listed on the funnel page' : 'Not listed on the funnel page'}
                    </Typography>
                    <Typography variant='body2' style={{ fontSize: '13px', color: '#808080' }}>
                      {funnelListing.reason}
                    </Typography>
                  </Box>
                  {funnelListing.listed ? (
                    <CheckCircle style={{ fontSize: 18, color: theme.alert.green.light, marginTop: 2 }} />
                  ) : (
                    <Cancel style={{ fontSize: 18, color: theme.alert.red.light, marginTop: 2 }} />
                  )}
                </Box>
                {funnelListing.listed !== isVisible && (
                  <AlertCard variant='warning' title='The two lists disagree'>
                    <Typography variant='body2' style={{ fontSize: '13px' }}>
                      {funnelListing.listed
                        ? 'The funnel page advertises this task, but the volunteer above would not see it once signed in. The funnel page matches on any one of programs, activities or roles, while the signed in check requires all of them.'
                        : 'The funnel page does not advertise this task even though the volunteer above would see it once signed in.'}
                    </Typography>
                  </AlertCard>
                )}
                {!selectedFunnel?.showTaskList && (
                  <Typography variant='body2' style={{ fontSize: '13px', color: '#808080', marginTop: 12 }}>
                    This funnel has its task list turned off, so applicants never see this list in practice.
                  </Typography>
                )}
              </>
            )}

          {renderContentBox(
            <TuneRounded style={{ fontSize: '14px', paddingRight: '5px' }} />,
            'Task behaviour',
            'Settings that change when the task appears rather than who qualifies',
            showBehaviour,
            () => setShowBehaviour(!showBehaviour),
            <Grid container spacing={3}>
              {behaviourNotes.map((note) => (
                <Grid item xs={6} sm={4} key={note.label}>
                  <Typography className={classes.microLabel}>{note.label}</Typography>
                  <Typography className={classes.microValue}>{note.value}</Typography>
                </Grid>
              ))}
            </Grid>
          )}

          {renderContentBox(
            <DescriptionOutlined style={{ fontSize: '14px', paddingRight: '5px' }} />,
            'What they would see',
            totalItemCount === 0
              ? 'This task has no form items configured'
              : `${visibleItemCount} of ${totalItemCount} items shown for the selected ${programWordPlural.toLowerCase()}`,
            showForm,
            () => setShowForm(!showForm),
            <>
              {hiddenItems.length > 0 && (
                <Box mb={2}>
                  {hiddenItems.map((item) => (
                    <Box key={item.name} display='flex' alignItems='center' mb={0.5}>
                      <Chip size='small' label={item.name} style={{ marginRight: 8 }} />
                      <Typography style={{ fontSize: '13px', color: '#808080' }}>
                        {`hidden for ${item.programNames.join(', ')}`}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
              <Box style={{ opacity: isVisible ? 1 : 0.45 }}>
                {deserializedTaskItems ? (
                  <Formik initialValues={initialFormValues} onSubmit={() => {}}>
                    {({ values }) => (
                      <Form>
                        <TaskFormFieldList
                          taskItems={deserializedTaskItems}
                          values={values}
                          volunteerProgramIds={programIds}
                          disableAutoFocus
                        />
                        <Box display='flex' justifyContent='center'>
                          <Box width={720} display='flex' justifyContent='flex-start'>
                            <TabletButton
                              color='primary'
                              variant='contained'
                              size='large'
                              disabled
                              style={{ width: 256 }}
                            >
                              Submit
                            </TabletButton>
                          </Box>
                        </Box>
                      </Form>
                    )}
                  </Formik>
                ) : (
                  <Typography variant='body2' style={{ fontSize: '13px', color: '#808080' }}>
                    This task has no form fields configured.
                  </Typography>
                )}
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
